import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Film, ChevronRight, Clock, CheckCircle2, ShieldQuestion, FileText, Coins, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { saas, errMsg } from "@/lib/saasApi";
import { SupportLine } from "@/components/SupportLine";
import { useI18n, DATE_LOCALE, usePageTitle } from "@/i18n";

const STATE = {
  CONCEPT: { icon: FileText, cls: "bg-re-soft text-re-muted" },
  PROCESSING: { icon: Clock, cls: "bg-amber-100 text-amber-700" },
  COMPLETED: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700" },
  SUPPORT_REVIEW: { icon: ShieldQuestion, cls: "bg-blue-100 text-blue-700" },
};

const FILTER_IDS = ["all", "CONCEPT", "PROCESSING", "COMPLETED", "SUPPORT_REVIEW"];

function fmtDate(iso, locale = "en-GB") {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
}

export default function Dashboard() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  usePageTitle("titles.dashboard");
  const locale = DATE_LOCALE[lang] || "en-GB";
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    Promise.all([saas.summary(), saas.listProjects()])
      .then(([s, p]) => { setSummary(s.data); setProjects(p.data); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => (filter === "all" ? projects : projects.filter((p) => p.state === filter)), [projects, filter]);

  const credits = summary?.availableCredits;
  const state = summary?.billingState;
  const attention = ["past_due", "unpaid", "incomplete", "incomplete_expired", "paused"].includes(state);
  const ended = state === "cancelled";
  // Paid credits remain spendable even when the subscription is inactive — the only gate is
  // having at least one credit. Suspended accounts can't reach this screen (blocked at login).
  const canCreate = !!summary && credits >= 1;
  const attn = attention ? t(`dash.attn.${state}`) : null;

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* 1. Welcome + primary action */}
      <div className="rounded-3xl border border-re-line bg-white p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-lg">
            <h1 className="font-heading text-2xl font-bold text-re-charcoal md:text-3xl">{t("dash.welcomeTitle")}</h1>
            <p className="mt-2 text-sm text-re-muted md:text-base">{t("dash.welcomeSub")}</p>
          </div>
          {canCreate && (
            <Button onClick={() => nav("/projects/new")} data-testid="new-project" className="rounded-full bg-re-red px-7 py-6 text-base font-semibold text-white hover:bg-re-deepred">
              <Plus className="mr-2 h-5 w-5" /> {t("dash.newProject")}
            </Button>
          )}
        </div>
      </div>

      {/* 2. Credits card */}
      <div className="flex items-center justify-between rounded-2xl border border-re-line bg-white p-6" data-testid="credit-card">
        <div>
          <p className="text-sm text-re-muted">{t("dash.creditsLabel")}</p>
          <p className="font-heading text-3xl font-bold text-re-charcoal" data-testid="available-credits">
            {summary ? summary.availableCredits : "—"} <span className="text-base font-medium text-re-muted">{t("dash.projectCredits")}</span>
          </p>
          {summary?.complimentaryCredit ? (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-re-red/10 px-2.5 py-1 text-xs font-medium text-re-red" data-testid="complimentary-credit-label">
              <Gift className="h-3.5 w-3.5" /> {t("dash.complimentary")}
            </p>
          ) : (
            <p className="mt-1 text-xs text-re-muted">{t("dash.creditExplainer")}</p>
          )}
        </div>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-re-red/10"><Coins className="h-6 w-6 text-re-red" /></span>
      </div>

      {/* 3. Billing attention banner — past_due / unpaid / incomplete / incomplete_expired / paused */}
      {attention && attn && (
        <div className="rounded-3xl border border-amber-300 bg-amber-50 p-6 md:p-8" data-testid="billing-blocked-banner">
          <h2 className="font-heading text-xl font-bold text-re-charcoal md:text-2xl" data-testid="billing-blocked-title">{attn.title}</h2>
          <p className="mt-2 max-w-xl text-sm text-re-muted md:text-base" data-testid="billing-blocked-message">{attn.text}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={() => nav("/account/billing")} data-testid="banner-manage-billing" className="rounded-full bg-re-charcoal px-6 py-5 text-base font-semibold text-white hover:bg-black">{attn.cta}</Button>
            <Button variant="outline" onClick={() => nav("/account/billing")} data-testid="banner-view-billing" className="rounded-full px-6 py-5 text-base font-semibold">{t("dash.viewBilling")}</Button>
          </div>
        </div>
      )}

      {/* 3b. Cancellation-scheduled info banner — active until the access date (non-blocking) */}
      {!!summary && summary.cancelScheduled && !attention && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-6 md:p-8" data-testid="cancel-scheduled-banner">
          <h2 className="font-heading text-xl font-bold text-re-charcoal md:text-2xl">{t("dash.cancelTitle")}</h2>
          <p className="mt-2 max-w-xl text-sm text-re-muted md:text-base" data-testid="cancel-scheduled-message">{t("dash.cancelText", { date: fmtDate(summary.billingDate, locale) })}</p>
          <div className="mt-5">
            <Button onClick={() => nav("/account/billing")} data-testid="banner-manage-subscription" className="rounded-full bg-re-charcoal px-6 py-5 text-base font-semibold text-white hover:bg-black">{t("dash.manageSubscription")}</Button>
          </div>
        </div>
      )}

      {/* 3c-1. Subscription ended but PAID credits remain — informational, non-blocking. */}
      {!!summary && !attention && ended && credits > 0 && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50/60 p-6 md:p-8" data-testid="ended-with-credits-banner">
          <h2 className="font-heading text-xl font-bold text-re-charcoal md:text-2xl">{t("dash.endedTitle")}</h2>
          <p className="mt-2 max-w-xl text-sm text-re-muted md:text-base" data-testid="ended-with-credits-message">
            {t("dash.endedText", { n: credits, cw: t(credits === 1 ? "dash.cw1" : "dash.cwN") })}
          </p>
          <div className="mt-5">
            <Button onClick={() => nav("/account/billing")} data-testid="banner-view-plans" className="rounded-full bg-re-charcoal px-6 py-5 text-base font-semibold text-white hover:bg-black">{t("dash.viewPlans")}</Button>
          </div>
        </div>
      )}

      {/* 3c-2. Zero credits — choose a plan to receive new project credits. */}
      {!!summary && !attention && credits === 0 && (
        <div className="rounded-3xl border border-re-red/20 bg-re-red/5 p-6 md:p-8" data-testid="plan-banner">
          <h2 className="font-heading text-xl font-bold text-re-charcoal md:text-2xl">{ended || summary.demoCreditUsed ? t("dash.zeroTitleMore") : t("dash.zeroTitleStart")}</h2>
          <p className="mt-2 max-w-xl text-sm text-re-muted md:text-base">
            {ended
              ? t("dash.zeroTextEnded")
              : summary.demoCreditUsed
                ? t("dash.zeroTextDemo")
                : t("dash.zeroTextNew")}
          </p>
          <div className="mt-5">
            <Button onClick={() => nav("/account/billing")} data-testid="banner-view-plans" className="rounded-full bg-re-red px-6 py-5 text-base font-semibold text-white hover:bg-re-deepred">{t("dash.viewPlans")}</Button>
          </div>
        </div>
      )}

      {/* 4. Projects */}
      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-semibold text-re-charcoal">{t("dash.projectsHeading")}</h2>
          <div className="flex flex-wrap gap-1.5" data-testid="project-filters">
            {FILTER_IDS.map((id) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`filter-${id}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${filter === id ? "bg-re-charcoal text-white" : "border border-re-line bg-white text-re-muted hover:text-re-charcoal"}`}>{t(`dash.filters.${id}`)}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-re-muted">{t("common.loading")}</p>
        ) : error ? (
          <p className="text-sm text-re-red" data-testid="dashboard-error">{error}</p>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-re-line bg-white p-10 text-center" data-testid="projects-empty">
            <Film className="mx-auto h-8 w-8 text-re-muted" />
            <p className="mt-3 font-medium text-re-charcoal">{t("dash.noProjects")}</p>
            <p className="mt-1 text-sm text-re-muted">{canCreate ? t("dash.emptyCanCreate") : t("dash.emptyNoCredits")}</p>
            {canCreate && (
              <Button onClick={() => nav("/projects/new")} data-testid="empty-create" className="mt-4 rounded-full bg-re-red text-white hover:bg-re-deepred"><Plus className="mr-2 h-4 w-4" /> {t("dash.createFirst")}</Button>
            )}
          </div>
        ) : shown.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-re-line bg-white p-8 text-center text-sm text-re-muted" data-testid="filter-empty">{t("dash.filterEmpty")}</p>
        ) : (
          <ul className="space-y-2" data-testid="projects-list">
            {shown.map((p) => {
              const st = STATE[p.state] || STATE.CONCEPT;
              const Icon = st.icon;
              return (
                <li key={p.id}>
                  <Link to={`/projects/${p.id}`} data-testid={`project-${p.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-re-line bg-white px-5 py-4 transition-colors hover:border-re-red/40">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-re-charcoal">{p.projectName}</p>
                      <p className="mt-0.5 text-xs text-re-muted">{p.ratio === "portrait" ? t("np.portrait") : t("np.landscape")} · {p.photoCount} {t("np.summaryPhotos").toLowerCase()} · {fmtDate(p.createdAt, locale)}</p>
                      {p.state === "PROCESSING" && (
                        <>
                          {typeof p.progress === "number" && (
                            <div className="mt-2 h-1.5 w-40 max-w-full overflow-hidden rounded-full bg-re-soft"><div className="h-full rounded-full bg-re-red" style={{ width: `${Math.max(5, Math.min(99, p.progress))}%` }} /></div>
                          )}
                          <p className="mt-1.5 text-xs text-re-muted" data-testid={`processing-hint-${p.id}`}>{t("dash.processingHint")}</p>
                        </>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className={`hidden items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium sm:inline-flex ${st.cls}`}>
                        {p.state === "PROCESSING" ? <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" /> : <Icon className="h-3.5 w-3.5" />} {t(`dash.states.${p.state in STATE ? p.state : "CONCEPT"}`)}
                      </span>
                      <span className="text-xs font-medium text-re-red">{t("dash.openProject")}</span>
                      <ChevronRight className="h-4 w-4 text-re-muted" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <SupportLine className="pt-2" testid="dashboard-support" />
    </div>
  );
}
